import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MultiSelect } from "@/components/ui/multi-select";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import { userService } from "@/services/userService";
import { OrgMember, User } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, Loader2, UserMinus, UserPlus, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface ManageTeamDialogProps {
  open: boolean;
  onClose: () => void;
  currentReports: OrgMember[];
}

export const ManageTeamDialog = ({ open, onClose, currentReports }: ManageTeamDialogProps) => {
  const [userSearch, setUserSearch] = useState("");
  const [pendingChanges, setPendingChanges] = useState<{ add: string[], remove: string[] }>({ add: [], remove: [] });
  const debouncedUserSearch = useDebounce(userSearch, 300);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Derive selected PIDs from currentReports with optimistic updates
  const selectedPids = useMemo(() => {
    const basePids = currentReports.map(r => r.pid);
    // Apply optimistic changes
    return basePids
      .filter(pid => !pendingChanges.remove.includes(pid))
      .concat(pendingChanges.add);
  }, [currentReports, pendingChanges]);

  // Fetch users for search
  const { data: usersResponse, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["users-team-management", debouncedUserSearch],
    queryFn: () => userService.getCompanyUsers(debouncedUserSearch, 1, 10),
    enabled: open,
  });

  // Combine search results with current reports
  const allAvailableUsers = useMemo(() => {
    const userMap = new Map<string, User>();
    const searchResults = usersResponse?.data || [];

    // Add current reports first
    currentReports.forEach(report => {
      userMap.set(report.pid, {
        pid: report.pid,
        fullName: report.fullName,
        email: report.email,
        avatarUrl: report.avatarUrl,
        position: report.position,
      } as User);
    });

    // Add search results (excluding current user)
    searchResults.forEach(u => {
      if (u.pid !== user?.pid) {
        userMap.set(u.pid, u);
      }
    });

    return Array.from(userMap.values());
  }, [usersResponse?.data, currentReports, user?.pid]);

  // Add mutation
  const addMutation = useMutation({
    mutationFn: async (userPid: string) => {
      if (!user?.pid) throw new Error("No user found");
      return userService.assignManager(userPid, user.pid);
    },
    onSuccess: async (_data, userPid) => {
      // Wait for queries to refetch before clearing pending changes
      await queryClient.invalidateQueries({ queryKey: ["direct-reports"] });
      await queryClient.invalidateQueries({ queryKey: ["team-priorities"] });
      // Clear pending changes after data is refetched
      setPendingChanges(prev => ({
        add: prev.add.filter(pid => pid !== userPid),
        remove: prev.remove
      }));
    },
    onError: (error, userPid) => {
      console.error("Error adding team member:", error);
      toast({
        title: "Error",
        description: "Failed to add team member. Please try again.",
        variant: "destructive",
      });
      // Revert optimistic change
      setPendingChanges(prev => ({
        add: prev.add.filter(pid => pid !== userPid),
        remove: prev.remove
      }));
    },
  });

  // Remove mutation
  const removeMutation = useMutation({
    mutationFn: async (userPid: string) => {
      return userService.removeManager(userPid);
    },
    onSuccess: async (_data, userPid) => {
      // Wait for queries to refetch before clearing pending changes
      await queryClient.invalidateQueries({ queryKey: ["direct-reports"] });
      await queryClient.invalidateQueries({ queryKey: ["team-priorities"] });
      // Clear pending changes after data is refetched
      setPendingChanges(prev => ({
        add: prev.add,
        remove: prev.remove.filter(pid => pid !== userPid)
      }));
    },
    onError: (error, userPid) => {
      console.error("Error removing team member:", error);
      toast({
        title: "Error",
        description: "Failed to remove team member. Please try again.",
        variant: "destructive",
      });
      // Revert optimistic change
      setPendingChanges(prev => ({
        add: prev.add,
        remove: prev.remove.filter(pid => pid !== userPid)
      }));
    },
  });

  // Handle selection changes
  const handleSelectionChange = (newSelectedPids: string[]) => {
    // Prevent changes during ongoing mutations
    if (isUpdating) return;
    
    // Find newly added
    const added = newSelectedPids.find(pid => !selectedPids.includes(pid));
    if (added) {
      // Add to pending and trigger mutation
      setPendingChanges(prev => ({
        add: [...prev.add, added],
        remove: prev.remove
      }));
      addMutation.mutate(added);
      return;
    }

    // Find newly removed
    const removed = selectedPids.find(pid => !newSelectedPids.includes(pid));
    if (removed) {
      // Add to pending and trigger mutation
      setPendingChanges(prev => ({
        add: prev.add,
        remove: [...prev.remove, removed]
      }));
      removeMutation.mutate(removed);
    }
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setUserSearch("");
      setPendingChanges({ add: [], remove: [] });
    }
  }, [open]);

  const isUpdating = addMutation.isPending || removeMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[650px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            Manage Your Team
          </DialogTitle>
          <DialogDescription className="text-base">
            Select people who report directly to you. Changes are saved automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Team Members Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold flex items-center gap-2">
                Team Members
                <Badge variant="secondary" className="font-normal">
                  {selectedPids.length}
                </Badge>
              </label>
            </div>

            <MultiSelect
              options={allAvailableUsers.map(u => ({
                label: u.fullName,
                value: u.pid,
                avatarUrl: u.avatarUrl,
                email: u.email
              }))}
              selected={selectedPids}
              onChange={handleSelectionChange}
              onSearchChange={setUserSearch}
              loading={isLoadingUsers || isUpdating}
              placeholder="Search by name..."
              initialOptions={currentReports.map(r => ({
                label: r.fullName,
                value: r.pid,
                avatarUrl: r.avatarUrl,
                email: r.email
              }))}
            />

            {/* Status Messages */}
            <div className="min-h-[20px]">
              {isUpdating && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground animate-in fade-in-50 duration-200">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Updating team members...</span>
                </div>
              )}
              {!isUpdating && selectedPids.length === 0 && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5" />
                  <span>No team members selected. Search and select users above.</span>
                </div>
              )}
            </div>
          </div>

          {/* Quick Tips */}
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Quick Tips:</p>
            <div className="space-y-1.5">
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <UserPlus className="h-3.5 w-3.5 mt-0.5 shrink-0 text-green-600 dark:text-green-500" />
                <span>Select a user to add them as your direct report</span>
              </div>
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <UserMinus className="h-3.5 w-3.5 mt-0.5 shrink-0 text-orange-600 dark:text-orange-500" />
                <span>Deselect a user to remove them from your team</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
